import type { ChatAttachmentInput } from "./types.js";

// The limits the backend holds a chat attachment to as well; keep both sides equal.
export const MAX_ATTACHMENT_COUNT = 4;
export const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;
export const SUPPORTED_ATTACHMENT_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
]);

/** A PNG or JPG of at most 5 MB; the one check for picker, paste and drop (#385). */
export function isSupportedAttachmentFile(
  file: Pick<File, "type" | "size">,
): boolean {
  return (
    SUPPORTED_ATTACHMENT_TYPES.has(file.type.toLowerCase()) &&
    file.size <= MAX_ATTACHMENT_SIZE
  );
}

export type AfasAttachmentFile = {
  id: string;
  filename: string;
  size?: number;
  mimeType?: string;
  previewUrl?: string;
};

export function toAfasFile(
  attachment: ChatAttachmentInput,
  index: number,
  prefix = "attachment",
): AfasAttachmentFile {
  const contentType = getAttachmentContentType(attachment);
  const previewUrl = getAttachmentPreviewUrl(attachment, contentType);
  return {
    id: `${prefix}-${index}-${attachment.fileName}-${attachment.size}`,
    filename: attachment.fileName,
    size: attachment.size,
    mimeType: contentType,
    ...(previewUrl ? { previewUrl } : {}),
  };
}

function getAttachmentContentType(attachment: ChatAttachmentInput): string {
  return typeof attachment.contentType === "string" && attachment.contentType
    ? attachment.contentType
    : "application/octet-stream";
}

function getAttachmentPreviewUrl(
  attachment: ChatAttachmentInput,
  contentType: string,
): string | undefined {
  const data = attachment.data;
  if (!data || !contentType.toLowerCase().startsWith("image/")) {
    return undefined;
  }
  return `data:${contentType};base64,${data}`;
}

/**
 * Drops attachments whose bytes are already attached (or repeated within `incoming`). The AI
 * Platform identifies an attachment by its checksum and refuses a message that carries the same
 * one twice ("Duplicate attachment ids are not allowed"). Pasting the same screenshot again, or
 * picking a file that is already attached, gives identical bytes under another name, so the
 * comparison is on content, never on the file name.
 */
export function withoutDuplicateAttachments(
  existing: readonly ChatAttachmentInput[],
  incoming: readonly ChatAttachmentInput[],
): ChatAttachmentInput[] {
  const seen = new Set(existing.map((attachment) => attachment.data ?? ""));
  return incoming.filter((attachment) => {
    const data = attachment.data ?? "";
    if (seen.has(data)) return false;
    seen.add(data);
    return true;
  });
}

/**
 * The new, distinct attachments that still fit within `maxCount`, and how many did not.
 * Repeats are dropped first: they neither take a slot nor count as overflow.
 */
export function admitNewAttachments(
  existing: readonly ChatAttachmentInput[],
  incoming: readonly ChatAttachmentInput[],
  maxCount: number,
): { admitted: ChatAttachmentInput[]; overflow: number } {
  const freeSlots = Math.max(0, maxCount - existing.length);
  const fresh = withoutDuplicateAttachments(existing, incoming);
  return {
    admitted: fresh.slice(0, freeSlots),
    overflow: Math.max(0, fresh.length - freeSlots),
  };
}
